import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { title: "", body: "", date: today(), order: 0 };

export default function AnnouncementsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startAdd = () => { setForm({ ...EMPTY, order: (items[items.length - 1]?.order || 0) + 1 }); setEditing("new"); };
  const startEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditing(it.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const onChange = (e) => {
    const { name, value, type } = e.target;
    setForm((f) => ({ ...f, [name]: type === "number" ? Number(value) : value }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") { await api.post("/admin/announcements", form); toast.success("Announcement published"); }
      else { await api.patch(`/admin/announcements/${editing}`, form); toast.success("Announcement updated"); }
      cancel();
      await load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="announcements-admin">
      <header className="flex items-end justify-between gap-4 mb-7">
        <div>
          <p className="adm-eyebrow mb-1.5">Nettside</p>
          <h1 className="adm-title">Announcements</h1>
          <p className="adm-sub mt-1.5">Short news items. The public strip is hidden when there are none.</p>
        </div>
        <button onClick={startAdd} className="adm-btn" data-testid="announcements-admin-add">+ Add announcement</button>
      </header>

      {editing && (
        <form onSubmit={save} className="adm-card p-6 mb-6 grid grid-cols-2 gap-4" data-testid="announcements-admin-form">
          <label className="col-span-2 adm-label">
            Title
            <input name="title" value={form.title} onChange={onChange} required maxLength={200} className="adm-input" data-testid="announcements-input-title" />
          </label>
          <label className="col-span-2 adm-label">
            Short text
            <textarea name="body" value={form.body} onChange={onChange} rows={3} className="adm-input" data-testid="announcements-input-body" />
          </label>
          <label className="adm-label">
            Date
            <input name="date" type="date" value={form.date} onChange={onChange} required className="adm-input" data-testid="announcements-input-date" />
          </label>
          <label className="adm-label">
            Display order
            <input name="order" type="number" value={form.order} onChange={onChange} className="adm-input" data-testid="announcements-input-order" />
          </label>
          <div className="col-span-2 flex items-center gap-3 pt-2">
            <button type="submit" className="adm-btn" data-testid="announcements-admin-save">
              {editing === "new" ? "Publish" : "Save changes"}
            </button>
            <button type="button" onClick={cancel} className="adm-btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      <div className="adm-card overflow-hidden">
        <table className="adm-table">
          <thead>
            <tr>
              <th className="w-16">#</th>
              <th className="w-32">Date</th>
              <th>Title / body</th>
              <th className="w-40 adm-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-6 text-center text-neutral-500 text-xs">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-neutral-500 text-xs" data-testid="announcements-admin-empty">No announcements yet. The public strip will stay hidden.</td></tr>}
            {items.map((a, i) => (
              <tr key={a.id} data-testid={`announcements-admin-row-${i}`}>
                <td className="tabular-nums text-neutral-400">{a.order}</td>
                <td className="tabular-nums text-neutral-500">{a.date}</td>
                <td>
                  <div className="font-medium">{a.title}</div>
                  {a.body && <div className="text-xs text-neutral-500 mt-0.5">{a.body}</div>}
                </td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => startEdit(a)} className="adm-btn-ghost adm-btn-sm mr-2" data-testid={`announcements-admin-edit-${i}`}>Edit</button>
                  <button onClick={() => remove(a.id)} className="adm-btn-danger adm-btn-sm" data-testid={`announcements-admin-delete-${i}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
